import { Job, Snapshot, SnapshotType } from '@analyzer/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useService } from '../providers/ServiceProvider';

export function useSnapshotCreateMutation(type: SnapshotType) {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation<Job, Error, { projectId: string; resourceId: string; settings?: any }>({
    mutationFn: ({ projectId, resourceId, settings }) =>
      client.snapshotCreate(type, projectId, resourceId, settings),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`${type}-snapshot-list`] })
  });
}

type QueryParams = { limit?: number; offset?: number };

export function useSnapshotListQuery(
  type: SnapshotType,
  projectId: string,
  resourceId: string,
  queryParams: QueryParams = {}
) {
  const client = useService();
  return useQuery<Snapshot[], Error>({
    queryKey: [`${type}-snapshot-list`, projectId, resourceId, queryParams],
    queryFn: () => client.snapshotList(type, projectId, resourceId, queryParams),
    keepPreviousData: true
  });
}

export function useSnapshotReadQuery(type: SnapshotType, projectId: string, snapshotId: string) {
  const client = useService();
  return useQuery<Snapshot, Error>({
    queryKey: [`${type}-snapshot-read`, projectId, snapshotId],
    queryFn: () => client.snapshotRead(type, projectId, snapshotId)
  });
}

export function useSnapshotDestroyMutation(type: SnapshotType) {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { projectId: string; snapshotId: string }>({
    mutationFn: (data) => client.snapshotDestroy(type, data.projectId, data.snapshotId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`${type}-snapshot-list`] })
  });
}

export function useWebSnapshotArchiveMutation() {
  const client = useService();
  return useMutation<{ url: string }, Error, { projectId: string; snapshotId: string }>({
    mutationFn: ({ projectId, snapshotId }) => client.webSnapshotArchive(projectId, snapshotId)
  });
}
